import React, { useCallback } from "react";
import { EditorObject } from "../types/editorTypes";

interface TextEditOverlayProps {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  editingTextPosition: { x: number; y: number; width: number; height: number };
  editingTextValue: string;
  setEditingTextValue: (value: string) => void;
  setCursorPosition: (pos: number) => void;
  handleTextEditConfirm: (newHeight?: number) => void;
  editingObj: EditorObject | undefined;
  // デフォルト値用
  textFontSize: number;
  textFontFamily: string;
  textFontStyle: 'normal' | 'bold' | 'italic';
  textColor: string;
}

/**
 * テキストを視覚的な行に分割する（\nとchar wrappingを考慮）
 */
const getVisualLines = (
  text: string,
  fontSize: number,
  fontFamily: string,
  fontStyle: string,
  textWidth: number,
): { text: string; startIndex: number }[] => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return [{ text, startIndex: 0 }];

  const fontWeight = fontStyle === 'bold' ? 'bold ' : fontStyle === 'italic' ? 'italic ' : '';
  ctx.font = `${fontWeight}${fontSize}px ${fontFamily}`;

  const visualLines: { text: string; startIndex: number }[] = [];
  const paragraphs = text.split('\n');
  let globalIndex = 0;

  for (const para of paragraphs) {
    if (para.length === 0) {
      visualLines.push({ text: '', startIndex: globalIndex });
    } else {
      let lineStart = 0;
      while (lineStart < para.length) {
        let lineEnd = lineStart + 1;
        while (lineEnd <= para.length) {
          if (ctx.measureText(para.slice(lineStart, lineEnd)).width > textWidth && lineEnd - lineStart > 1) {
            lineEnd--;
            break;
          }
          lineEnd++;
        }
        lineEnd = Math.min(lineEnd, para.length);
        visualLines.push({
          text: para.slice(lineStart, lineEnd),
          startIndex: globalIndex + lineStart,
        });
        lineStart = lineEnd;
      }
    }
    globalIndex += para.length + 1; // +1 for \n
  }

  return visualLines.length > 0 ? visualLines : [{ text: '', startIndex: 0 }];
};

export const TextEditOverlay: React.FC<TextEditOverlayProps> = ({
  textareaRef,
  editingTextPosition,
  editingTextValue,
  setEditingTextValue,
  setCursorPosition,
  handleTextEditConfirm,
  editingObj,
  textFontSize,
  textFontFamily,
  textFontStyle,
  textColor,
}) => {
  // 全ての矢印キーを明示的に処理（隠れたtextareaのネイティブ動作に依存しない）
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleTextEditConfirm();
      return;
    }

    const textarea = textareaRef.current;
    if (!textarea) return;

    // 左右矢印キー
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const currentPos = textarea.selectionStart ?? 0;
      let newPos: number;
      if (e.key === 'ArrowLeft') {
        newPos = Math.max(0, currentPos - 1);
      } else {
        newPos = Math.min(editingTextValue.length, currentPos + 1);
      }
      setCursorPosition(newPos);
      textarea.setSelectionRange(newPos, newPos);
      return;
    }

    // 上下矢印キー: 視覚的な行に基づいて移動
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();

      const currentPos = textarea.selectionStart ?? 0;
      const fontSize = editingObj?.fontSize || textFontSize;
      const fontFamily = editingObj?.fontFamily || textFontFamily;
      const fontStyle = editingObj?.fontStyle || textFontStyle;
      const textWidth = editingTextPosition.width;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const fontWeight = fontStyle === 'bold' ? 'bold ' : fontStyle === 'italic' ? 'italic ' : '';
      ctx.font = `${fontWeight}${fontSize}px ${fontFamily}`;

      const visualLines = getVisualLines(editingTextValue, fontSize, fontFamily, fontStyle, textWidth);

      // 現在のカーソルがどの行にいるか特定
      let currentLineIdx = 0;
      for (let i = 0; i < visualLines.length; i++) {
        const line = visualLines[i];
        const lineEnd = line.startIndex + line.text.length;
        if (currentPos >= line.startIndex && currentPos <= lineEnd) {
          currentLineIdx = i;
          break;
        }
      }

      // 現在行内のx座標を計算
      const currentLine = visualLines[currentLineIdx];
      const posInLine = currentPos - currentLine.startIndex;
      const xOffset = ctx.measureText(currentLine.text.slice(0, posInLine)).width;

      // 移動先の行
      let targetLineIdx;
      if (e.key === 'ArrowUp') {
        targetLineIdx = Math.max(0, currentLineIdx - 1);
      } else {
        targetLineIdx = Math.min(visualLines.length - 1, currentLineIdx + 1);
      }

      if (targetLineIdx === currentLineIdx) return;

      const targetLine = visualLines[targetLineIdx];

      // 移動先の行で同じx座標に近い文字位置を探す
      let bestPos = 0;
      for (let i = 0; i <= targetLine.text.length; i++) {
        const w = ctx.measureText(targetLine.text.slice(0, i)).width;
        if (w >= xOffset) {
          const prevW = i > 0 ? ctx.measureText(targetLine.text.slice(0, i - 1)).width : 0;
          bestPos = (xOffset - prevW < w - xOffset) ? Math.max(0, i - 1) : i;
          break;
        }
        bestPos = i;
      }

      const newPos = targetLine.startIndex + bestPos;
      setCursorPosition(newPos);
      textarea.setSelectionRange(newPos, newPos);
    }
  }, [handleTextEditConfirm, editingTextValue, editingObj, textFontSize, textFontFamily, textFontStyle, editingTextPosition, setCursorPosition, textareaRef]);

  return (
    <textarea
      ref={textareaRef}
      value={editingTextValue}
      onChange={(e) => {
        setEditingTextValue(e.target.value);
        // 入力後のカーソル位置を同期
        setCursorPosition(e.target.selectionStart ?? 0);
      }}
      onKeyDown={handleKeyDown}
      style={{
        position: 'fixed',
        left: '-9999px',
        top: '-9999px',
        width: '1px',
        height: '1px',
        opacity: 0,
        pointerEvents: 'auto',
        resize: 'none',
      }}
      autoFocus
    />
  );
};

export default TextEditOverlay;
